import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { adsApi, categoriesApi, fileUploadApi, promotionPackagesApi, authApi, paymentApi, CreateAdRequest } from '../services/api';
import toast from 'react-hot-toast';
import DealdenLoader from '../components/DealdenLoader';

interface AdForm {
  title: string;
  description: string;
  price: number;
  categoryId: string;
  subCategoryId?: string;
  city: string;
  province: string;
  postalCode: string;
  address?: string;
  images?: FileList;
  [key: string]: any; // Allow dynamic attribute fields
}

// Geocode address using Geoapify Autocomplete API (returns first match lat/lng)
// Uses the Geoapify autocomplete endpoint as requested. If the API call fails or returns no result,
// falls back to a default coordinate (Toronto).
const geocodeAddress = async (city: string, province: string, postalCode: string): Promise<{ lat: number; lng: number }> => {
  const defaultCoords = { lat: 43.6532, lng: -79.3832 }; // Toronto fallback

  try {
    const apiKey = '4029fbc311864b87b98cb258e033c1a9'; // Provided API key
    const text = [city, province, postalCode].filter(Boolean).join(' ');
    if (!text) return defaultCoords;

    const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(text)}&apiKey=${apiKey}`;

    const resp = await fetch(url);
    if (!resp.ok) {     
      return defaultCoords;
    }

    const data = await resp.json();

    // Geoapify returns GeoJSON-like features. Try several locations for lat/lng.
    const feature = data?.features?.[0];
    if (feature) {
      // geometry.coordinates is [lon, lat]
      if (feature.geometry && Array.isArray(feature.geometry.coordinates)) {
        const [lon, lat] = feature.geometry.coordinates;
        if (typeof lat === 'number' && typeof lon === 'number') {
          return { lat, lng: lon };
        }
      }

      // properties.lat / properties.lon (some responses include these)
      if (feature.properties && typeof feature.properties.lat === 'number' && typeof feature.properties.lon === 'number') {
        return { lat: feature.properties.lat, lng: feature.properties.lon };
      }
    }

    return defaultCoords;
  } catch (error) {  
    return { lat: 43.6532, lng: -79.3832 };
  }
};

const CreateAdPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [categoryError, setCategoryError] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');
  const [selectedSubSubCategory, setSelectedSubSubCategory] = useState<string>('');
  const [categoryHierarchy, setCategoryHierarchy] = useState<string[]>([]);
  const [selectedPromotionPackage, setSelectedPromotionPackage] = useState<string>('');
  const [selectedPromotionPackageData, setSelectedPromotionPackageData] = useState<any>(null);
  const [pendingPayment, setPendingPayment] = useState<boolean>(false);
  const [marketingCode, setMarketingCode] = useState<string>('');
  const [uploadedImages, setUploadedImages] = useState<any[]>([]);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [aiImageIndex, setAiImageIndex] = useState<number | null>(null);
  const [aiGenerating, setAiGenerating] = useState<boolean>(false);
  const [createdAdId, setCreatedAdId] = useState<string>('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [blockReason, setBlockReason] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<AdForm>();

  // Address autocomplete state (Geoapify)
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const addressDebounceRef = useRef<number | null>(null);

  const fetchAddressSuggestions = async (text: string) => {
    try {
      if (!text || text.trim().length < 3) {
        setAddressSuggestions([]);
        return;
      }
      const apiKey = '4029fbc311864b87b98cb258e033c1a9';
      const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(text)}&limit=5&apiKey=${apiKey}`;
      const resp = await fetch(url);
      if (!resp.ok) return setAddressSuggestions([]);
      const data = await resp.json();
      const features = data?.features || [];

      const suggestions = features.map((f: any) => {
        const props = f.properties || {};
        return {
          formatted: props.formatted || props.name || f?.properties?.address || '',
          city: props.city || props.county || props.state || '',
          state: props.state || '',
          postcode: props.postcode || props.postalcode || props.postal_code || '',
          lat: f?.geometry?.coordinates?.[1],
          lon: f?.geometry?.coordinates?.[0]
        };
      });

      setAddressSuggestions(suggestions.filter((s: any) => s.formatted));
      setShowAddressSuggestions(true);
    } catch (err) {      
      setAddressSuggestions([]);
    }
  };

  const handleAddressInputChange = (value: string) => {
    setAddressQuery(value);
    if (addressDebounceRef.current) {
      window.clearTimeout(addressDebounceRef.current);
    }
    // debounce
    addressDebounceRef.current = window.setTimeout(() => {
      fetchAddressSuggestions(value);
    }, 300) as unknown as number;
  };

  const selectAddressSuggestion = (s: any) => {
    // Fill form fields with selected suggestion
    setAddressQuery(s.formatted || '');
    setValue('address', s.formatted || '');
    if (s.city) setValue('city', s.city);
    if (s.state) setValue('province', s.state);
    if (s.postcode) setValue('postalCode', s.postcode);
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
  };

  // NOTE: Do not pre-register fields that appear only on later steps (address/postalCode).
  // They will be registered inline on the inputs when their step is mounted so they are
  // not validated prematurely. This helps per-step validation semantics.

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.getAll()
  });

  const { data: categoryAttributes } = useQuery({
    queryKey: ['category-attributes', selectedSubSubCategory || selectedSubCategory || selectedCategory],
    queryFn: () => categoriesApi.getAttributes(selectedSubSubCategory || selectedSubCategory || selectedCategory),
    enabled: !!(selectedSubSubCategory || selectedSubCategory || selectedCategory)
  });

  const { data: userPlans } = useQuery({
    queryKey: ['user-plans'],
    queryFn: async () => {
      const response = await paymentApi.getUserPlans();
              // User plans loaded successfully
      return response;
    }
  });

  // If the user has no active plans with remaining ads, redirect them to the Plans page
  // This enforces purchasing/selecting a plan before posting an ad.
  useEffect(() => {
    if (!userPlans) return; // wait until the query resolves

    try {
      const plans = Array.isArray(userPlans.data) ? userPlans.data : [];
      const hasAvailablePlan = plans.some((plan: any) => plan.isActive && !plan.isExpired && plan.hasRemainingAds);
      if (!hasAvailablePlan) {
        // Set a blocking reason so the UI can show an explanatory message
        setBlockReason('You have no active plans with remaining ad slots. To post an ad you must purchase or activate a plan.');
      } else {
        // Clear blocking reason when user has an available plan
        setBlockReason(null);
      }
    } catch (err) {
      // If something unexpected happens, do nothing here and allow normal flow
    
    }
  }, [userPlans]);

  const { data: promotionPackages } = useQuery({
    queryKey: ['promotion-packages'],
    queryFn: () => promotionPackagesApi.getActive(),
    enabled: !userPlans?.data || userPlans.data.length === 0 || !userPlans.data.some(plan => plan.isActive && !plan.isExpired && plan.hasRemainingAds)
  });

  // Mutation for activating free plans (called when user selects a free package)
  const activateFreePlanMutation = useMutation({
    mutationFn: async (packageId: string) => {
      const response = await paymentApi.activateFreePlan(packageId);
      return response.data;
    },
    onSuccess: async (data) => {
      toast.success('Free plan activated successfully!');
      // Refresh user plans so UI reflects newly activated plan
      await queryClient.invalidateQueries({ queryKey: ['user-plans'] });
      // Refetch to ensure we have the latest data
      await queryClient.refetchQueries({ queryKey: ['user-plans'] });

      // If backend returns a planId, use it to set the selected promotion package
      if (data?.planId) {
        setSelectedPromotionPackage(data.planId);
        const pkg = promotionPackages?.data?.find((p: any) => p.id === data.planId);
        if (pkg) setSelectedPromotionPackageData(pkg);
      }
    },
    onError: () => {
      toast.error('Error activating free plan');
    }
  });

  // Handle payment return
  useEffect(() => {
    const paymentStatus = searchParams.get('paymentStatus');
    const sessionId = searchParams.get('sessionId');
    
    if (paymentStatus) {
      if (paymentStatus === 'success' && sessionId) {
        // Verify payment through backend
        const verifyPayment = async () => {
          try {
            toast.loading('Verifying payment...');
            
            const response = await paymentApi.verifyPayment(sessionId);
            
            toast.dismiss();
            
            if (response.data.status === 'success' && response.data.payment) {
              const payment = response.data.payment;
              const packageId = payment.promotionPackageId;
              
              if (packageId) {
                setSelectedPromotionPackage(packageId);
                setPendingPayment(false);
                
                // Find the package data
                const pkg = promotionPackages?.data?.find(p => p.id === packageId);
                if (pkg) {
                  setSelectedPromotionPackageData(pkg);
                  toast.success(`Payment successful! Package "${pkg.name}" activated.`);
                }
              }
              
              // Set step to 3 to show the confirmation
              setStep(3);
            } else {
              toast.error('Payment verification failed.');
              setStep(3);
            }
          } catch (error: any) {
            toast.dismiss();
            toast.error(error.response?.data?.message || 'Payment verification error');
            setStep(3);
          }
        };
        
        verifyPayment();
      } else if (paymentStatus === 'cancelled') {
        toast('Payment was cancelled.');
        setStep(3);
      } else if (paymentStatus === 'failed') {
        toast.error('Payment failed. Please try again.');
        setStep(3);
      }
      
      // Clean up URL parameters
      navigate('/post-ad', { replace: true });
    }
  }, [searchParams, promotionPackages, navigate]);

  const createAdMutation = useMutation({
    mutationFn: (data: CreateAdRequest) => adsApi.create(data),
    onSuccess: async (response) => {
      const adId = response.data?.id;
      const adSlug = response.data?.slug;
      
      // Invalidate user plans query to refresh the data after ad creation
      queryClient.invalidateQueries({ queryKey: ['user-plans'] });
      
      if (!adId) {
        toast.error('Error getting ad ID. Please try again.');
        return;
      }
      
      setCreatedAdId(adId);
      
      // Navigate directly to the ad page if no images to upload
      if (selectedImages.length === 0) {
        setIsPublishing(false);
        toast.dismiss(); // Dismiss any loading toasts
                  toast.success('Your ad has been successfully posted!');
        // Use slug-based URL if available, fallback to ID
        const adUrl = adSlug ? `/ads/${adSlug}` : `/ad/${adId}`;
        navigate(adUrl);
        return;
      }

      // Upload selected images after ad creation
      if (selectedImages.length > 0) {   
        
        // Check authentication status before uploading
        try {
          const authResponse = await authApi.checkAuth();
          // Authentication status checked - if we get here, user is authenticated
        } catch (error) {
          // If checkAuth fails, user is not authenticated
          toast.error('To upload images, you must log into your account. Please log in and try again.');
          setStep(4);
          return;
        }

    
        
        // Add longer delay to ensure ad is fully committed to database
        await new Promise(resolve => setTimeout(resolve, 3000));
        
                  toast('📤 Uploading images...', { 
          duration: 8000,
          style: { direction: 'ltr' }
        });
        try {
          // Process uploads sequentially to avoid overwhelming the server
          for (let i = 0; i < selectedImages.length; i++) {
            const file = selectedImages[i];
            const isPrimary = i === 0; // First image is primary   
            
            try {
              // Check if file is image or video and use appropriate endpoint
              if (file.type.startsWith('image/')) {
                            // Uploading image file
                const result = await fileUploadApi.uploadImage(adId, file, isPrimary);
              
              } else if (file.type.startsWith('video/')) {
                // Uploading video file
                const result = await fileUploadApi.uploadVideo(adId, file);
             
              } else {
             
                toast.error(`Unsupported file type: ${file.name}`);
                continue;
              }
              
              // Add small delay between uploads to avoid server overload
              if (i < selectedImages.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 500));
              }
            } catch (uploadError: any) {
             
              
              // Show specific error message from API (including Persian messages)
              const errorMessage = uploadError.response?.data?.message || `Error uploading ${file.name}`;
              toast.error(errorMessage, {
                duration: 6000,
                style: { direction: 'ltr', fontFamily: 'Arial, sans-serif' }
              });
              
              // Continue with other files instead of stopping all uploads
              continue;
            }
          }
          toast.dismiss(); // Dismiss any loading toasts
          setIsPublishing(false);
                      toast.success('Your ad was successfully posted and images were uploaded!');
                  } catch (error: any) {           
            toast.dismiss(); // Dismiss any loading toasts
          
          // Don't show errors that might confuse users - just let them know they can add images later
          toast.dismiss(); // Dismiss any processing toasts
          setIsPublishing(false);
          toast('Ad posted successfully! You can add images later from the edit page.', {
            duration: 5000,
            icon: '⚠️'
          });
        }
      }
      
      // Navigate directly to the ad page after successful creation and image upload
      // Use slug-based URL if available, fallback to ID
      const adUrl = adSlug ? `/ads/${adSlug}` : `/ad/${adId}`;
      navigate(adUrl);
    },
    onError: (error: any) => {
      setIsPublishing(false);
      toast.dismiss(); // Dismiss any loading toasts
      toast.error(error.response?.data?.message || 'Ad creation failed.');
    }
  });

  const onSubmit = async (data: AdForm) => {
    // onSubmit entry (debug log removed)
    if (step < 4) {
      // Validate step 1: Category selection
      if (step === 1) {
              if (!selectedCategory) {
        // show inline error in case toasts are not visible
        setCategoryError('Please select a category');
        // also keep toast for visibility when it works
        try { toast.error('Please select a category'); } catch {}
        return;
        }
      }
      
      // Validate step 2: Title and description
      if (step === 2) {
              if (!data.title || !data.description) {
        toast.error('Please fill all required fields (title, description)');
        return;
        }
      }
      
      setStep(step + 1);
      return;
    }

    if (step === 4) {
      // Set publishing state immediately when user clicks انتشار آگهی
      setIsPublishing(true);
      // Validate all required fields before submission
      if (!selectedCategory) {
        setIsPublishing(false);
        toast.error('Please select a category');
        setStep(1);
        return;
      }

      if (!data.title || !data.description) {
        setIsPublishing(false);
        toast.error('Please fill all required fields (title, description)');
        setStep(2);
        return;
      }

      if (!data.city || !data.province || !data.postalCode || !data.price) {
        setIsPublishing(false);
        toast.error('Please fill required fields (city, province, postal code, price)');
        return;
      }

      if (!selectedPromotionPackage) {     
        setIsPublishing(false);
        toast.error('Please select a promotion plan');
        setStep(3);
        return;
      }

      if (pendingPayment) {
        setIsPublishing(false);
        toast.error('Please complete payment for the selected package first');
        setStep(3);
        return;
      }

      try {
        // Get coordinates for the location
        const coordinates = await geocodeAddress(data.city, data.province, data.postalCode);
        
        const adData: CreateAdRequest = {
          title: data.title.trim(),
          description: data.description.trim(),
          price: Number(data.price),
          categoryId: selectedSubSubCategory || selectedSubCategory || selectedCategory,
          subCategoryId: undefined, // Use only categoryId for the deepest selection
          city: data.city.trim(),
          province: data.province,
          postalCode: data.postalCode.trim(),
          address: data.address?.trim() || `${data.city.trim()}, ${data.province}`,
          latitude: coordinates.lat,
          longitude: coordinates.lng,
          attributes: categoryAttributes?.data?.reduce((acc: any, attr) => {
            const value = (data as any)[`attr_${attr.id}`];
            if (value) {
              acc[attr.id] = value;
            }
            return acc;
          }, {}) || {},
          promotionPackageId: selectedPromotionPackage,
          marketingCode: marketingCode.trim() || undefined
        };
      
        // Show loading message for the entire process (without blocking overlay)
        toast('⏳ Creating ad...', { 
          duration: 6000,
          style: { direction: 'ltr' }
        });
        
        createAdMutation.mutate(adData);
      } catch (error) {
          setIsPublishing(false);
        toast.error('Error processing location. Please try again.');
      }
    }
  };

  const handleFileUpload = async (files: FileList) => {
    const fileArray = Array.from(files);
    const maxFiles = 5;
    
    // Check if total files would exceed limit
    if (selectedImages.length + fileArray.length > maxFiles) {
      toast.error(`You can only upload up to ${maxFiles} files`);
      return;
    }
    
    // Validate file types, sizes, and filenames
    const validFiles: File[] = [];
    const maxSize = 10 * 1024 * 1024; // 10MB for videos, 5MB for images
    
    for (const file of fileArray) {
      // Check if file is image or video
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        toast.error(`${file.name} is not a valid image or video`);
        continue;
      }
      
      // Validate filename characters (same validation as backend)
      const allowedCharsPattern = /^[a-zA-Z0-9\-_\.\s]+$/;
      if (!allowedCharsPattern.test(file.name)) {
        toast.error(`File name "${file.name}" contains invalid characters. Please use only English letters, numbers, hyphens (-) and underscores (_).`, {
          duration: 6000,
          style: { direction: 'ltr', fontFamily: 'Arial, sans-serif' }
        });
        continue;
      }
      
      // Different size limits for images vs videos
      const sizeLimit = file.type.startsWith('video/') ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > sizeLimit) {
        const limitMB = file.type.startsWith('video/') ? '10MB' : '5MB';
        toast.error(`${file.name} is too large. Maximum size is ${limitMB}MB`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (validFiles.length === 0) return;
    
    // Create previews for valid files
    const newPreviews: string[] = [];
    for (const file of validFiles) {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push(e.target?.result as string);
        // Update state when all previews are loaded
        if (newPreviews.length === validFiles.length) {
          setSelectedImages(prev => [...prev, ...validFiles]);
          setImagePreviews(prev => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    }
  };
  
  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    // If the removed image was the one used for AI, clear the selection
    setAiImageIndex(prev => (prev === index ? null : (prev && prev > index ? prev - 1 : prev)));
  };

  const handleGenerateFromImage = async (index: number) => {
    // Only one image can be used for AI generation
    const file = selectedImages[index];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file for AI generation');
      return;
    }

    try {
      setAiGenerating(true);
      toast.loading('Generating title and description from the image...');
      const resp = await fileUploadApi.uploadImageTemp(file);
      toast.dismiss();
      const aiTitle = resp.data?.title;
      const aiDescription = resp.data?.description;
      if (aiTitle) {
        // Only prefill if user hasn't typed a title yet
        const currentTitle = (watch('title') || '').trim();
        if (!currentTitle) setValue('title', aiTitle);
      }
      if (aiDescription) {
        const currentDesc = (watch('description') || '').trim();
        if (!currentDesc) setValue('description', aiDescription);
      }
      setAiImageIndex(index);
      toast.success('AI suggestions applied (you can edit them)');
    } catch (error: any) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || 'Error generating AI metadata');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleCategoryChange = (categoryId: string) => {    
    setSelectedCategory(categoryId);
    setSelectedSubCategory('');
    setSelectedSubSubCategory('');
    setCategoryHierarchy([categoryId]);
    setValue('categoryId', categoryId);
    if (categoryId) setCategoryError('');
  };

  const handleSubCategoryChange = (subCategoryId: string) => {
    setSelectedSubCategory(subCategoryId);
    setSelectedSubSubCategory('');
    setCategoryHierarchy([selectedCategory, subCategoryId]);
  };

  const handleSubSubCategoryChange = (subSubCategoryId: string) => {
    setSelectedSubSubCategory(subSubCategoryId);
    setCategoryHierarchy([selectedCategory, selectedSubCategory, subSubCategoryId]);
  };

  const handlePromotionPackageSelect = async (pkg: any) => {
    // Ensure only one plan can be selected at a time
    if (selectedPromotionPackage === pkg.id) {
      // If clicking the same package, deselect it
      setSelectedPromotionPackage('');
      setSelectedPromotionPackageData(null);
      setPendingPayment(false);
      return;
    }

    setSelectedPromotionPackage(pkg.id);
    setSelectedPromotionPackageData(pkg);

    // Check if it's a paid plan
    if (pkg.price > 0) {
      // For paid plans, we need to handle payment
      setPendingPayment(true);
      toast(`Package ${pkg.name} selected. Payment of $${pkg.price.toFixed(2)} is required to complete.`);
      
      // Create secure payment session through backend
      try {
        toast('💳 Creating payment session...', { 
          duration: 5000,
          style: { direction: 'ltr' }
        });
        
        const response = await paymentApi.createSession({
          packageId: pkg.id,
          packageName: pkg.name,
          amount: pkg.price
        });
        
        toast.dismiss();
                  toast.success('Redirecting to payment gateway...');
        
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkoutUrl;
        
      } catch (error: any) {
        toast.dismiss();
                  toast.error(error.response?.data?.message || 'Error creating payment session');
        setSelectedPromotionPackage('');
        setSelectedPromotionPackageData(null);
        setPendingPayment(false);
      }
    } else {
      // Free plan, no payment needed
      setPendingPayment(false);
              toast.success(`Free package "${pkg.name}" selected.`);
      // Activate free plan on the backend (same behavior as PlansPage)
      try {
        toast.loading('Activating free plan...');
        activateFreePlanMutation.mutate(pkg.id);
      } catch (err) {
        // mutation handles errors via onError
      }
    }
  };

  // Get parent categories (those without parentCategoryId)
  const parentCategories = categories?.data?.filter(cat => !cat.parentCategoryId) || [];
  
  // Get subcategories for selected parent category
  const availableSubCategories = categories?.data?.filter(cat => cat.parentCategoryId === selectedCategory) || [];
  
  // Get sub-subcategories for selected subcategory
  const availableSubSubCategories = categories?.data?.filter(cat => cat.parentCategoryId === selectedSubCategory) || [];

  if (blockReason) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-start justify-center" dir="ltr">
        <div className="w-full max-w-xl px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-10 text-center border border-gray-100">
            <div className="mx-auto flex items-center justify-center w-20 h-20 rounded-full bg-red-50 mb-4">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <defs>
                  <linearGradient id="g1" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0%" stopColor="#EF4444" />
                    <stop offset="100%" stopColor="#DC2626" />
                  </linearGradient>
                </defs>
                <path d="M12 9v4" stroke="url(#g1)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M12 17h.01" stroke="url(#g1)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10.29 3.86 2.88 18.14a1.5 1.5 0 0 0 1.29 2.26h15.66a1.5 1.5 0 0 0 1.29-2.26L13.71 3.86a1.5 1.5 0 0 0-2.42 0z" stroke="url(#g1)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="rgba(239,68,68,0.06)" />
              </svg>
            </div>

            <h2 className="text-3xl font-extrabold text-gray-900 mb-2">Cannot Post Ad</h2>
            <p className="text-gray-600 mb-6 text-lg">{blockReason}</p>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => navigate('/plans')}
                className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-md focus:outline-none focus:ring-2 focus:ring-red-200"
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M12 5v14M5 12h14" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                View Plans
              </button>

              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center px-6 py-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors focus:outline-none"
              >
                Back to Home
              </button>
            </div>

            <p className="text-xs text-gray-400 mt-6">If you need help choosing a plan, visit the Help page or contact our support.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* DealdenLoader with dim overlay during submission */}
      {(isSubmitting || isPublishing) && (
        <DealdenLoader 
          fullScreen={true}
          size="large" 
          message="Publishing ad..." 
        />
      )}
      
      <div className="min-h-screen bg-gray-50 py-8" dir="ltr">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Progress Bar */}
          <div className="bg-gray-50 px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-semibold text-gray-900">Post Ad</h1>
              <div className="flex items-center space-x-4">
                {[1, 2, 3, 4].map((stepNumber) => (
                  <div
                    key={stepNumber}
                    className={`flex items-center ${stepNumber < 4 ? 'mr-4' : ''}`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        step >= stepNumber
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {stepNumber}
                    </div>
                    {stepNumber < 4 && (
                      <div
                        className={`w-16 h-1 ${
                          step > stepNumber ? 'bg-primary-600' : 'bg-gray-200'
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-2 text-sm text-gray-600">
              {step === 1 && 'Select Category and Add Images'}
              {step === 2 && 'Product Description'}
              {step === 3 && 'Select Promotion Plan'}
              {step === 4 && 'Set Price and Location'}
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-6">
            {/* Step 1: Category and Photos */}
            {step === 1 && (
              <div className="space-y-6">
                {/* Cascading Category Selection */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white bg-no-repeat bg-right-4 bg-[length:12px] pl-3 pr-10"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.75rem center'
                      }}
                    >
                                              <option value="">Select Category</option>
                      {parentCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    {categoryError && (
                      <p className="mt-2 text-sm text-red-600">{categoryError}</p>
                    )}
                  </div>

                  {availableSubCategories.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subcategory
                      </label>
                      <select
                        value={selectedSubCategory}
                        onChange={(e) => handleSubCategoryChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white bg-no-repeat bg-right-4 bg-[length:12px] pl-3 pr-10"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.75rem center'
                        }}
                      >
                        <option value="">Select Subcategory (Optional)</option>
                        {availableSubCategories.map((subCategory) => (
                          <option key={subCategory.id} value={subCategory.id}>
                            {subCategory.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {availableSubSubCategories.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sub-subcategory
                      </label>
                      <select
                        value={selectedSubSubCategory}
                        onChange={(e) => handleSubSubCategoryChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white bg-no-repeat bg-right-4 bg-[length:12px] pl-3 pr-10"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.75rem center'
                        }}
                      >
                        <option value="">Select Sub-subcategory (Optional)</option>
                        {availableSubSubCategories.map((subSubCategory) => (
                          <option key={subSubCategory.id} value={subSubCategory.id}>
                            {subSubCategory.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Display selected category path */}
                  {selectedCategory && (
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                                              <p className="text-sm text-gray-600 mb-1">Selected Category:</p>
                      <div className="flex items-center text-sm font-medium text-primary-600">
                                               {categoryHierarchy.map((catId, index) => {
                         const category = categories?.data?.find(c => c.id === catId);
                         
                         return (
                           <span key={catId} className="flex items-center">
                             {category?.name}
                             {index < categoryHierarchy.length - 1 && (
                               <svg className="w-4 h-4 mx-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                               </svg>
                             )}
                           </span>
                         );
                       })}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Images
                  </label>
                  
                  {/* Image Previews */}
                  {imagePreviews.length > 0 && (
                    <div className="mb-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {imagePreviews.map((preview, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={preview}
                              alt={`پیش‌نمایش ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg border border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                            {/* <div className="absolute left-2 top-2">
                              {aiImageIndex === index ? (
                                <span className="inline-flex items-center px-2 py-1 bg-primary-600 text-white text-xs rounded">AI</span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleGenerateFromImage(index)}
                                  disabled={aiGenerating}
                                  className="inline-flex items-center px-2 py-1 bg-white text-xs rounded border border-gray-200 hover:bg-gray-50"
                                >
                                  {aiGenerating && aiImageIndex === index ? (
                                    <svg className="animate-spin h-4 w-4 mr-1" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                                    </svg>
                                  ) : null}
                                  Use for AI
                                </button>
                              )}
                            </div> */}
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        {selectedImages.length} of 5 images selected
                      </p>
                    </div>
                  )}
                  
                  {/* Upload Area */}
                  {selectedImages.length < 5 && (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                      <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="mt-4">
                        <label htmlFor="images" className="cursor-pointer">
                          <span className="mt-2 block text-sm font-medium text-gray-900">
                            {selectedImages.length === 0 ? 'Upload up to 5 files (Images and Video)' : `Add more files (${5 - selectedImages.length} remaining)`}
                          </span>
                          <span className="mt-1 block text-sm text-gray-500">
                                                          Images: PNG, JPG, GIF up to 5MB | Video: MP4, MOV up to 10MB
                          </span>
                          <input
                            id="images"
                            type="file"
                            multiple
                            accept="image/*,video/*"
                            className="sr-only"
                            onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                          />
                        </label>
                      </div>
                    </div>
                  )}
                  
                  {selectedImages.length >= 5 && (
                    <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg">
                                                <p className="text-sm text-green-700">Maximum number of images selected (5/5)</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Title and Description */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    {...register('title', { required: 'Title is required', maxLength: { value: 100, message: 'Title must be at most 100 characters' } })}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="What are you selling?"
                    maxLength={100}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.title ? (
                      <p className="text-sm text-red-600">{errors.title.message}</p>
                    ) : (
                      <div />
                    )}
                    <div className="text-xs text-gray-500">
                      {(watch('title') || '').length}/100
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    {...register('description', { required: 'Description is required', maxLength: { value: 2000, message: 'Description must be at most 2000 characters' } })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Describe your product in detail..."
                    maxLength={2000}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.description ? (
                      <p className="text-sm text-red-600">{errors.description.message}</p>
                    ) : (
                      <div />
                    )}
                    <div className="text-xs text-gray-500">
                      {(watch('description') || '').length}/2000
                    </div>
                  </div>
                </div>

                {/* Category-specific attributes */}
                {categoryAttributes?.data?.map((attr) => (
                  <div key={attr.id}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {attr.label} {attr.isRequired && '*'}
                    </label>
                    {attr.type === 'Text' && (
                      <input
                        {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} الزامی است` : false
                        })}
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={attr.defaultValue || ''}
                      />
                    )}
                    {attr.type === 'Number' && (
                      <input
                        {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} الزامی است` : false
                        })}
                        type="number"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={attr.defaultValue || ''}
                      />
                    )}
                    {attr.type === 'TextArea' && (
                      <textarea
                        {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} الزامی است` : false
                        })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={attr.defaultValue || ''}
                      />
                    )}
                    {attr.type === 'Dropdown' && attr.options && (
                      <select
                        {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} is required` : false
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white bg-no-repeat bg-right-4 bg-[length:12px] pl-3 pr-10"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.75rem center'
                        }}
                      >
                        <option value="">Select {attr.label}</option>
                        {(() => {
                          try {
                            return JSON.parse(attr.options).map((option: string) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ));
                          } catch {
                            return attr.options.split('\n').filter(opt => opt.trim()).map((option: string) => (
                              <option key={option.trim()} value={option.trim()}>
                                {option.trim()}
                              </option>
                            ));
                          }
                        })()}
                      </select>
                    )}
                    {attr.type === 'Boolean' && (
                      <div className="flex items-center space-x-3">
                        <input
                          {...register(`attr_${attr.id}` as any)}
                          type="checkbox"
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-600">{attr.label}</span>
                      </div>
                    )}
                    {attr.type === 'Date' && (
                      <input
                        {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} الزامی است` : false
                        })}
                        type="date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    )}
                    {attr.type === 'MultiSelect' && attr.options && (
                      <div className="space-y-2">
                        {(() => {
                          try {
                            return JSON.parse(attr.options).map((option: string) => (
                              <div key={option} className="flex items-center space-x-3">
                                <input
                                  {...register(`attr_${attr.id}` as any)}
                                  type="checkbox"
                                  value={option}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-700">{option}</span>
                              </div>
                            ));
                          } catch {
                            return attr.options.split('\n').filter(opt => opt.trim()).map((option: string) => (
                              <div key={option.trim()} className="flex items-center space-x-3">
                                <input
                                  {...register(`attr_${attr.id}` as any)}
                                  type="checkbox"
                                  value={option.trim()}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-700">{option.trim()}</span>
                              </div>
                            ));
                          }
                        })()}
                      </div>
                    )}
                {attr.type === 'Url' && (
                      <input
                         {...register(`attr_${attr.id}` as any, {
                          required: attr.isRequired ? `${attr.label} is required` : false,
                          pattern: {
                              value: /^https?:\/\/.+/,
                              message: 'Please enter a valid URL address'
                 }
                })}
                        type="url"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="https://example.com"
                      />
 )}

            {attr.type === 'Email' && (
  <input
    {...register(`attr_${attr.id}` as any, {
      required: attr.isRequired ? `${attr.label} is required` : false,
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: 'Please enter a valid email address'
      }
    })}
    type="email"
    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
    placeholder="example@email.com"
  />
)}

            {attr.type === 'Date' && (
  <input
    {...register(`attr_${attr.id}` as any, {
      required: attr.isRequired ? `${attr.label} is required` : false
    })}
    type="date"
    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
  />
)}

            {attr.type === 'Email' && (
  <input
    {...register(`attr_${attr.id}` as any, {
      required: attr.isRequired ? `${attr.label} is required` : false,
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: 'Please enter a valid email address'
      }
    })}
    type="email"
    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
    placeholder={attr.defaultValue || ''}
  />
)}

      {attr.type === 'Url' && (
  <input
    {...register(`attr_${attr.id}` as any, {
      required: attr.isRequired ? `${attr.label} is required` : false,
      pattern: {
        value: /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
        message: 'Please enter a valid website address'
      }
    })}
    type="url"
    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
    placeholder={attr.defaultValue || 'https://'}
  />
)}

                    {errors[`attr_${attr.id}`] && (
                      <p className="mt-1 text-sm text-red-600">
                        {(errors[`attr_${attr.id}`] as any)?.message}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Step 3: Promotion Package Selection */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">Select one of your available plans</h2>
                  <p className="text-gray-600">
                                          {userPlans?.data && userPlans.data.some(plan => plan.isActive && !plan.isExpired && plan.hasRemainingAds)
                        ? 'Select one of your available plans:'
                        : 'Please select a plan for your ad. (Required)'
                    }
                  </p>
                </div>

                {/* Show User's Own Plans if Available */}
                {userPlans?.data && userPlans.data.some(plan => plan.isActive && !plan.isExpired && plan.hasRemainingAds) ? (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Plans:</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {userPlans.data
                        .filter(plan => plan.isActive && !plan.isExpired && plan.hasRemainingAds)
                        .map((plan) => (
                          <div
                            key={plan.id}
                            className={`border rounded-lg p-6 hover:border-${plan.colorClass}-300 transition-colors cursor-pointer relative ${
                              selectedPromotionPackage === plan.promotionPackageId 
                                ? `border-2 border-${plan.colorClass}-500 bg-${plan.colorClass}-50`
                                : 'border-gray-200'
                            }`}
                            onClick={() => {
                              const packageId = plan.promotionPackageId || plan.id;                            
                              setSelectedPromotionPackage(packageId);
                              setSelectedPromotionPackageData(plan);
                              setPendingPayment(false);
                            }}
                          >
                            {selectedPromotionPackage === plan.promotionPackageId && (
                              <div className="absolute -top-3 right-4">
                                <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                                  Selected
                                </span>
                              </div>
                            )}
                     <div className="text-center">
  <div className={`w-12 h-12 bg-${plan.colorClass}-100 rounded-full flex items-center justify-center mx-auto mb-4`}>
    <span className="text-2xl">{plan.iconEmoji}</span>
  </div>
  <h3 className="text-lg font-semibold text-gray-900 mb-2">{plan.name}</h3>
  <div className="text-sm text-green-600 font-medium mb-2">Purchased Plan</div>
  <div className="text-sm text-gray-600 mb-4">
    Remaining: {plan.remainingAdsCount} of {plan.maxAdsCount} ads
  </div>
  <div className="text-sm text-gray-500 mb-4">
    Expiration: {new Date(plan.expirationDate).toLocaleDateString('en-US')}
  </div>
  <ul className="text-sm text-gray-600 space-y-2 text-left">
    {plan.features.split(',').map((feature, index) => (
      <li key={index} className="flex items-center gap-2">
        <span className="text-green-500">✓</span>
        {feature.trim()}
      </li>
    ))}
  </ul>
</div>

                          </div>
                        ))}
                    </div>
                    
                    {/* Option to buy new plans */}
                 <div className="mt-8 p-4 bg-gray-50 rounded-lg">
  <p className="text-sm text-gray-600 mb-3">Need a new plan?</p>
  <button
    type="button"
    onClick={() => navigate('/plans')}
    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
  >
    View and purchase new plans →
  </button>
</div>

                  </div>
                ) : promotionPackages?.data && promotionPackages.data.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {promotionPackages.data.map((pkg) => (
                      <div
                        key={pkg.id}
                        className={`border rounded-lg p-6 transition-colors cursor-pointer relative ${
                          selectedPromotionPackage === pkg.id
                            ? pendingPayment && pkg.price > 0
                              ? `border-2 border-orange-500 bg-orange-50`
                              : `border-2 border-${pkg.colorClass}-500 bg-${pkg.colorClass}-50`
                            : 'border-gray-200'
                        }`}
                        onClick={() => handlePromotionPackageSelect(pkg)}
                      >
                        {selectedPromotionPackage === pkg.id && (
                          <div className="absolute -top-3 right-4">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              pendingPayment && pkg.price > 0
                                ? 'bg-orange-500 text-white'
                                : 'bg-green-500 text-white'
                            }`}>
                              {pendingPayment && pkg.price > 0 ? 'Pending Payment' : 'Selected'}
                            </span>
                          </div>
                        )}
                        {pkg.isPopular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
  <span className={`bg-${pkg.colorClass}-500 text-white px-3 py-1 rounded-full text-sm font-medium`}>
    Most Popular
  </span>
</div>

                        )}
                        <div className="text-center">
                          <div className={`w-12 h-12 bg-${pkg.colorClass}-100 rounded-full flex items-center justify-center mx-auto mb-4`}>
                            <span className="text-2xl">{pkg.iconEmoji}</span>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">{pkg.name}</h3>
                          <div className="text-3xl font-semibold text-gray-900 mb-1">
                            {pkg.price === 0 ? 'Free' : `$${pkg.price.toFixed(2)}`}
                          </div>
                          {pkg.price > 0 && (
                            <div className="text-sm text-gray-500 mb-4">
                             for {pkg.durationDays} days
                            </div>
                          )}
                          <ul className="text-sm text-gray-600 space-y-2 mb-6 text-left">
                            {pkg.features.map((feature, index) => (
                              <li key={index}>{feature}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Loading promotion packages...</p>
                  </div>
                )}

                {/* Marketing Code Field (Optional) */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <label htmlFor="marketingCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Marketing Code (Optional)
                  </label>
                  <input
                    type="text"
                    id="marketingCode"
                    value={marketingCode}
                    onChange={(e) => setMarketingCode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter marketing code if you have one"
                    maxLength={50}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    If you have a marketing code from a marketing person, enter it here
                  </p>
                </div>
              </div>
            )}

            {/* Step 4: Price and Location */}
            {step === 4 && (
              <div className="space-y-6">
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
                    Price (CAD) *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      defaultValue={0}
                      {...register('price', {
                           
                          min: { value: 0, message: 'Price must be greater than 0' }
                      })}
                      type="number"
                      step="0.01"
                      dir="ltr"
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-left"
                      placeholder="0.00"
                    />
                  </div>
                  {errors.price && (
                    <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <div className="relative">
                    <input
                      {...register('address')}
                      type="text"
                      dir="ltr"
                      onChange={(e) => {
                        // keep react-hook-form in sync and trigger autocomplete
                        setValue('address', (e.target as HTMLInputElement).value);
                        handleAddressInputChange(e.target.value);
                      }}
                      onBlur={(e) => {
                        // ensure suggestions close
                        setTimeout(() => setShowAddressSuggestions(false), 150);
                      }}
                      onFocus={() => { if (addressSuggestions.length > 0) setShowAddressSuggestions(true); }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-left"
                      placeholder="123 Main Street (Optional - City/Province will be used if not provided)"
                      defaultValue={addressQuery}
                    />

                    {showAddressSuggestions && addressSuggestions.length > 0 && (
                      <ul className="absolute z-40 left-0 right-0 bg-white border border-gray-200 mt-1 rounded-md shadow-lg max-h-60 overflow-auto text-left">
                        {addressSuggestions.map((s, idx) => (
                          <li
                            key={idx}
                            onMouseDown={() => selectAddressSuggestion(s)}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                          >
                            <div className="text-sm text-gray-900">{s.formatted}</div>
                            <div className="text-xs text-gray-500">{[s.city, s.state, s.postcode].filter(Boolean).join(' • ')}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {errors.address && (
                    <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    <input
                                              {...register('city', { required: 'City is required' })}
                      type="text"
                      dir="ltr"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-left"
                      placeholder="Toronto"
                    />
                    {errors.city && (
                      <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="province" className="block text-sm font-medium text-gray-700 mb-2">
                      Province *
                    </label>
                    <select
                                              {...register('province', { required: 'Province is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white bg-no-repeat bg-right-4 bg-[length:12px] pl-3 pr-10"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.75rem center'
                      }}
                    >
                                              <option value="">Select Province</option>
                      <option value="ON">Ontario</option>
                      <option value="QC">Quebec</option>
                      <option value="BC">British Columbia</option>
                      <option value="AB">Alberta</option>
                      <option value="MB">Manitoba</option>
                      <option value="SK">Saskatchewan</option>
                      <option value="NS">Nova Scotia</option>
                      <option value="NB">New Brunswick</option>
                      <option value="NL">Newfoundland and Labrador</option>
                      <option value="PE">Prince Edward Island</option>
                      <option value="NT">Northwest Territories</option>
                      <option value="NU">Nunavut</option>
                      <option value="YT">Yukon</option>
                    </select>
                    {errors.province && (
                      <p className="mt-1 text-sm text-red-600">{errors.province.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Postal Code *
                  </label>
                  <input
                    {...register('postalCode', {
                      required: 'Postal code is required',
                      pattern: {
                        value: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
                        message: 'Please enter a valid postal code (e.g., M5V 3L9)'
                      }
                    })}
                    type="text"
                    dir="ltr"
                    onBlur={(e) => {
                      // normalize postal code: uppercase and insert space after 3 characters
                      const val = (e.target as HTMLInputElement).value || '';
                      let normalized = val.toUpperCase().trim();
                      // remove extra spaces
                      normalized = normalized.replace(/\s+/g, '');
                      if (normalized.length >= 6) {
                        normalized = normalized.slice(0,3) + ' ' + normalized.slice(3,6);
                      }
                      setValue('postalCode', normalized);
                    }}
                    onChange={(e) => {
                      // keep form state updated while typing
                      setValue('postalCode', (e.target as HTMLInputElement).value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-left"
                    placeholder="M5V 3L9"
                  />
                  {errors.postalCode && (
                    <p className="mt-1 text-sm text-red-600">{errors.postalCode.message}</p>
                  )}
                </div>
              </div>
            )}



            {/* Navigation Buttons */}
            {step <= 4 && (
              <div className="flex items-center justify-between pt-8 mt-8 border-t border-gray-200">
                {/* Previous Button Container */}
                <div className="flex">
                  {step > 1 && (
                    <button
                      type="button"
                      onClick={() => setStep(step - 1)}
                      className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors font-medium"
                    >
                      <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                      </svg>
                      Previous
                    </button>
                  )}
                </div>

                {/* Next Button Container */}
                <div className="flex">
                  <button
                    type="button"
                    onClick={() => {
                      // Ensure the react-hook-form submit handler runs even if native submit is blocked
                      handleSubmit(onSubmit)();
                    }}
                    disabled={isSubmitting || (step === 3 && pendingPayment)}
                    className="inline-flex items-center px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        {step === 3 && pendingPayment ? 'Waiting for payment completion...' : step < 4 ? 'Next' : 'Publish Ad'}
                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default CreateAdPage; 
